import type { RuleModule } from '../engine/registry';
import type { GameEventDraft, GameRuntime, GameState } from '../engine/state';
import { enterPhase } from '../engine/phase';
import type { AdvanceBag } from './advanceCards';
import type { ResourceBag } from '../engine/gameDef';
import type { SeatIdx } from '../types';

// Victory gating (outline §12; plan §7.5): never an action. The module's afterAction
// hook — LAST in module order, so award transfers land first — declares the win the
// moment the current phase's eligibleWinner holds trueVp ≥ target. Hidden Landmarks
// count (trueVp, not the public projection); entering PRE_ROLL is itself checked, which
// is exactly why an off-turn 10th point waits for that seat's turn start (§14.18).
// finalizeGame is shared with SYSTEM_END (plan §13.5, PT-6).

function sumVp(state: GameState, env: GameRuntime, seat: SeatIdx, includeHidden: boolean): number {
  let sum = 0;
  for (const src of env.composed.vpSources) {
    if (!env.def.victory.sources.includes(src.key)) continue;
    if (!includeHidden && src.hidden) continue;
    sum += src.compute(state, env, seat);
  }
  return sum;
}

/** A seat's TRUE victory points: the def's vpSource keys summed (hidden included). */
export function trueVp(state: GameState, env: GameRuntime, seat: SeatIdx): number {
  return sumVp(state, env, seat, true);
}

/**
 * A seat's PUBLIC victory points: the same sum with `hidden` sources SKIPPED — exactly the
 * number `viewFor` projects as `vpPublic` (plan §10.2), computed here from a GameState so
 * RULES may branch on it. Any rule whose legality every seat can observe (the Friendly
 * Bandit, outline §9 as amended 2026-08-18) MUST read this and never `trueVp`: branching on
 * hidden Landmarks would leak them through the legal-action set (brief invariant 5).
 */
export function publicVp(state: GameState, env: GameRuntime, seat: SeatIdx): number {
  return sumVp(state, env, seat, false);
}

/** Standings per plan §13.6: winner · non-resigned by trueVp desc (seat idx ties) · resigned by later-resignation-first. */
function computeStandings(state: GameState, env: GameRuntime, winner: SeatIdx): SeatIdx[] {
  const rest = state.seats.map((_, i) => i).filter((s) => s !== winner);
  const alive = rest.filter((s) => !state.seats[s]!.resigned);
  const gone = rest.filter((s) => state.seats[s]!.resigned);
  const vp = new Map(alive.map((s) => [s, trueVp(state, env, s)]));
  alive.sort((a, b) => vp.get(b)! - vp.get(a)! || a - b); // equal VP = shared class; display order by seat index
  gone.sort((a, b) => (state.seats[b]!.resignedAtSeq ?? 0) - (state.seats[a]!.resignedAtSeq ?? 0) || a - b); // earlier resignation ranks lower
  return [winner, ...alive, ...gone];
}

/** Seal the game: winner, standings, GAME_OVER phase + the full-disclosure event (D7). */
export function finalizeGame(state: GameState, env: GameRuntime, winner: SeatIdx, emit: (e: GameEventDraft) => void): void {
  state.winner = winner;
  state.standings = computeStandings(state, env, winner);
  enterPhase(state, env, { t: 'GAME_OVER', winner }, emit);
  const adv = state.bags['advance-cards'] as AdvanceBag | undefined;
  const effectOf = (card: string): string | null => {
    for (const deck of env.def.decks) {
      const hit = deck.cards.find((c) => c.card === card);
      if (hit) return hit.effect;
    }
    return null;
  };
  const reveal = state.seats.map((seat, i) => {
    const cards = adv?.perSeat[i]?.cards.map((c) => c.card) ?? [];
    const landmarks = cards.filter((c) => effectOf(c) === 'landmark');
    if (adv?.perSeat[i]) adv.perSeat[i]!.revealedLandmarks = landmarks; // the state-side reveal (PT-7)
    return {
      landmarks: landmarks.length,
      hand: { ...seat.hand } as ResourceBag,
      cards,
    };
  });
  emit({ kind: 'GAME_OVER', vis: 'public', winner, standings: [...state.standings], reveal });
}

/** The §6.7 per-map override, else the def's target (outline §15 names this surface). */
export function victoryTarget(env: GameRuntime): number {
  return env.map.overrides.victoryTarget ?? env.def.victory.target;
}

export const victoryModule: RuleModule = {
  id: 'victory',
  register(ctx) {
    ctx.afterAction((state, env, emit) => {
      if (state.phase.t === 'GAME_OVER') return; // already sealed
      const spec = env.composed.phaseStates.get(state.phase.t);
      const eligible = spec ? spec.eligibleWinner(state.phase) : null;
      if (eligible === null || state.seats[eligible]!.resigned) return;
      // H6-S6: honour a map's own target, matching every sibling override
      // (supplyStock in createGame, discardOver in bandit, piecePools in building).
      // It was schema-validated and copied into ResolvedMap, then read by nobody — so a
      // map asking for 13 still sealed silently at 10.
      if (trueVp(state, env, eligible) >= victoryTarget(env)) {
        finalizeGame(state, env, eligible, emit);
      }
    });
  },
};
